/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Web Audio Mixer Studio DTOs & OpenAPI 3.1 Schemas (Task 7.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Audio Track Types for the Multi-Track Studio.
 */
export const AudioMixerTrackTypeSchema = z
  .enum(['speech', 'bgm', 'sfx'])
  .openapi({
    description: 'Category of audio track: speech dialogues, background music, or sound effects',
    example: 'speech',
  });

export type AudioMixerTrackType = z.infer<typeof AudioMixerTrackTypeSchema>;

/**
 * Output Audio Container Formats for Mixer Rendering.
 */
export const AudioMixerOutputFormatSchema = z
  .enum(['wav', 'mp3'])
  .openapi({
    description: 'Target audio container format: uncompressed Lossless WAV (16-bit PCM) or compressed MP3',
    example: 'wav',
  });

export type AudioMixerOutputFormat = z.infer<typeof AudioMixerOutputFormatSchema>;

/**
 * Audio Clip placed onto an audio track timeline.
 */
export const AudioClipSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique clip ID (UUID)', example: 'clip_abc123' }),
    name: z.string().min(1).openapi({ description: 'Display name of clip', example: 'Intro Dialogue' }),
    audioUrl: z.string().url().openapi({
      description: 'Public or presigned URL of source audio asset',
      example: 'https://media.sophiaplatform.com/audio/ws_01/tts/sample.mp3',
    }),
    startTime: z.number().min(0).openapi({ description: 'Start time on timeline in seconds', example: 0.5 }),
    duration: z.number().positive().openapi({ description: 'Playback duration in seconds', example: 4.2 }),
    offset: z.number().min(0).default(0).openapi({ description: 'Offset in source audio file in seconds', example: 0 }),
    volume: z.number().min(0).max(2).default(1.0).openapi({ description: 'Volume multiplier (0.0 - 2.0)', example: 1.0 }),
    fadeInMs: z.number().min(0).default(0).openapi({ description: 'Fade-in duration in milliseconds', example: 200 }),
    fadeOutMs: z.number().min(0).default(0).openapi({ description: 'Fade-out duration in milliseconds', example: 300 }),
    duckingEnabled: z
      .boolean()
      .default(false)
      .openapi({ description: 'Whether this clip triggers BGM ducking (usually true for speech)', example: true }),
    loop: z.boolean().default(false).optional().openapi({ description: 'Whether this clip should loop to fill track timeline', example: false }),
  })
  .openapi('AudioClip');

export type AudioClipDto = z.infer<typeof AudioClipSchema>;

/**
 * Audio Track in the Multi-Track Mixer Studio.
 */
export const AudioMixerTrackSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique track ID', example: 'track_speech_01' }),
    name: z.string().min(1).openapi({ description: 'Track name', example: 'Voiceover (Rachel)' }),
    type: AudioMixerTrackTypeSchema,
    volume: z.number().min(0).max(2).default(1.0).openapi({ description: 'Track gain multiplier', example: 1.0 }),
    muted: z.boolean().default(false).openapi({ description: 'Whether track is muted', example: false }),
    solo: z.boolean().default(false).openapi({ description: 'Whether track is soloed', example: false }),
    pan: z.number().min(-1).max(1).default(0).openapi({ description: 'Stereo pan (-1.0 Left to +1.0 Right)', example: 0 }),
    loop: z.boolean().default(false).optional().openapi({ description: 'Whether track repeats audio clips to match total timeline length (BGM loop)', example: true }),
    clips: z.array(AudioClipSchema).default([]).openapi({ description: 'List of audio clips on this track' }),
  })
  .openapi('AudioMixerTrack');

export type AudioMixerTrackDto = z.infer<typeof AudioMixerTrackSchema>;

/**
 * Smart Audio Ducking Configuration.
 */
export const AudioDuckingSettingsSchema = z
  .object({
    enabled: z.boolean().default(true).openapi({ description: 'Whether voice-activated ducking is active', example: true }),
    duckingPercent: z
      .number()
      .min(0)
      .max(100)
      .default(20)
      .openapi({ description: 'Reduced BGM volume percentage during speech (0 - 100%)', example: 20 }),
    fadeDurationMs: z
      .number()
      .min(50)
      .max(2000)
      .default(250)
      .openapi({ description: 'Duration of transition into ducked state in ms', example: 250 }),
    recoveryDurationMs: z
      .number()
      .min(50)
      .max(2000)
      .default(400)
      .openapi({ description: 'Duration of recovery back to normal volume in ms', example: 400 }),
  })
  .openapi('AudioDuckingSettings');

export type AudioDuckingSettingsDto = z.infer<typeof AudioDuckingSettingsSchema>;

/**
 * Project definition for saving/loading in the database.
 */
export const AudioMixerProjectSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique mixer project ID (UUID)', example: 'proj_mix_12345' }),
    workspaceId: z.string().openapi({ description: 'Workspace owning the project', example: 'ws_001' }),
    userId: z.string().nullable().optional().openapi({ description: 'User creator ID', example: 'usr_001' }),
    title: z.string().min(1).max(200).openapi({ description: 'Project title', example: 'Episode 1: AI Revolution Podcast' }),
    description: z.string().max(1000).optional().openapi({ description: 'Project description', example: 'Intro + Dialogue' }),
    sampleRate: z
      .number()
      .int()
      .refine((val) => [44100, 48000].includes(val), { message: 'Sample rate must be 44100 or 48000 Hz' })
      .default(44100)
      .openapi({ description: 'Audio sample rate in Hz (44100 or 48000)', example: 44100 }),
    channels: z
      .number()
      .int()
      .min(1)
      .max(2)
      .default(2)
      .openapi({ description: 'Audio channels: 1 (Mono) or 2 (Stereo)', example: 2 }),
    masterVolume: z.number().min(0).max(2).default(1.0).openapi({ description: 'Master gain multiplier', example: 1.0 }),
    duration: z.number().min(0).default(0).openapi({ description: 'Total calculated project duration in seconds', example: 125.4 }),
    duckingSettings: AudioDuckingSettingsSchema.default({
      enabled: true,
      duckingPercent: 20,
      fadeDurationMs: 250,
      recoveryDurationMs: 400,
    }),
    tracks: z.array(AudioMixerTrackSchema).default([]).openapi({ description: 'Collection of audio tracks' }),
    createdAt: z.string().openapi({ description: 'ISO creation timestamp', example: '2026-10-21T10:00:00.000Z' }),
    updatedAt: z.string().openapi({ description: 'ISO update timestamp', example: '2026-10-21T10:00:00.000Z' }),
  })
  .openapi('AudioMixerProject');

export type AudioMixerProjectDto = z.infer<typeof AudioMixerProjectSchema>;

/**
 * Payload for creating or updating a mixer project.
 */
export const SaveAudioProjectSchema = z
  .object({
    title: z.string().min(1).max(200).openapi({ description: 'Project title', example: 'Marketing Video Voiceover' }),
    description: z.string().max(1000).optional().openapi({ description: 'Project description' }),
    sampleRate: z
      .number()
      .int()
      .refine((val) => [44100, 48000].includes(val), { message: 'Sample rate must be 44100 or 48000 Hz' })
      .default(44100),
    channels: z.number().int().min(1).max(2).default(2),
    masterVolume: z.number().min(0).max(2).default(1.0),
    duration: z.number().min(0).default(0),
    duckingSettings: AudioDuckingSettingsSchema.optional(),
    tracks: z.array(AudioMixerTrackSchema).default([]),
  })
  .openapi('SaveAudioProject');

export type SaveAudioProjectDto = z.infer<typeof SaveAudioProjectSchema>;

/**
 * Offline Audio Render Manifest for Client-side or Server-side Audio Engine.
 */
export const AudioMixerRenderManifestSchema = z
  .object({
    projectId: z.string().openapi({ description: 'Project ID being rendered', example: 'proj_mix_12345' }),
    title: z.string().openapi({ description: 'Output audio title', example: 'Podcast Master Mix' }),
    format: AudioMixerOutputFormatSchema.default('wav'),
    sampleRate: z.number().int().default(44100),
    channels: z.number().int().min(1).max(2).default(2),
    bitrate: z.number().int().optional().openapi({ description: 'MP3 Bitrate in kbps (128, 192, 256, 320)', example: 192 }),
    duckingSettings: AudioDuckingSettingsSchema.optional(),
    tracks: z.array(AudioMixerTrackSchema),
    masterVolume: z.number().min(0).max(2).default(1.0),
  })
  .openapi('AudioMixerRenderManifest');

export type AudioMixerRenderManifestDto = z.infer<typeof AudioMixerRenderManifestSchema>;

/**
 * Request Presigned Direct Upload URL for Client Rendered Audio.
 */
export const AudioMixerUploadUrlRequestSchema = z
  .object({
    projectId: z.string().min(1).openapi({ description: 'Associated mixer project ID', example: 'proj_mix_12345' }),
    filename: z.string().min(1).max(200).openapi({ description: 'Target filename', example: 'final-mix.wav' }),
    format: AudioMixerOutputFormatSchema.default('wav'),
    fileSizeBytes: z.number().int().positive().optional().openapi({ description: 'Rendered file size in bytes', example: 15482000 }),
    duration: z.number().positive().openapi({ description: 'Total rendered audio duration in seconds', example: 87.5 }),
  })
  .openapi('AudioMixerUploadUrlRequest');

export type AudioMixerUploadUrlRequestDto = z.infer<typeof AudioMixerUploadUrlRequestSchema>;

/**
 * Response containing Direct Upload URL to Cloudflare R2.
 */
export const AudioMixerUploadUrlResponseSchema = z
  .object({
    uploadUrl: z.string().openapi({
      description: 'Direct PUT endpoint URL targeting Cloudflare R2 bucket',
      example: 'https://api.sophiaplatform.com/api/v1/workspaces/ws_01/voice/mixer/direct-upload?key=audio/...',
    }),
    r2Key: z.string().openapi({
      description: 'Destination key in Cloudflare R2 storage',
      example: 'audio/ws_01/mixes/proj_mix_12345_1729500000.wav',
    }),
    publicUrl: z.string().url().openapi({
      description: 'Public zero-egress CDN URL',
      example: 'https://media.sophiaplatform.com/audio/ws_01/mixes/proj_mix_12345_1729500000.wav',
    }),
    expiresInSeconds: z.number().int().openapi({ description: 'Upload authorization lifetime in seconds', example: 3600 }),
  })
  .openapi('AudioMixerUploadUrlResponse');

export type AudioMixerUploadUrlResponseDto = z.infer<typeof AudioMixerUploadUrlResponseSchema>;

/**
 * Request to publish a client-rendered audio file into Media Assets gallery.
 */
export const PublishAudioMixRequestSchema = z
  .object({
    projectId: z.string().min(1).openapi({ description: 'Mixer project ID', example: 'proj_mix_12345' }),
    title: z.string().min(1).max(200).openapi({ description: 'Asset display title', example: 'Final Podcast Audio' }),
    r2Key: z.string().min(1).openapi({ description: 'R2 storage key where file was uploaded', example: 'audio/ws_01/mixes/xyz.wav' }),
    publicUrl: z.string().url().openapi({ description: 'Public CDN URL of uploaded mix', example: 'https://media.sophiaplatform.com/audio/ws_01/mixes/xyz.wav' }),
    format: AudioMixerOutputFormatSchema.default('wav'),
    fileSizeBytes: z.number().int().min(0).default(0).openapi({ description: 'File size in bytes', example: 10485760 }),
    duration: z.number().min(0).default(0).openapi({ description: 'Audio duration in seconds', example: 60.5 }),
    channels: z.number().int().min(1).max(2).default(2),
    sampleRate: z.number().int().default(44100),
  })
  .openapi('PublishAudioMixRequest');

export type PublishAudioMixRequestDto = z.infer<typeof PublishAudioMixRequestSchema>;

/**
 * Response for published audio mix.
 */
export const PublishAudioMixResponseSchema = z
  .object({
    assetId: z.string().openapi({ description: 'Generated media asset ID', example: 'asset_mix_9988' }),
    publicUrl: z.string().url().openapi({ description: 'Zero-egress CDN URL', example: 'https://media.sophiaplatform.com/audio/ws_01/mixes/xyz.wav' }),
    r2Key: z.string().openapi({ description: 'R2 storage key', example: 'audio/ws_01/mixes/xyz.wav' }),
    mimeType: z.string().openapi({ description: 'MIME content type', example: 'audio/wav' }),
    publishedAt: z.string().openapi({ description: 'ISO publication timestamp', example: '2026-10-21T10:05:00.000Z' }),
  })
  .openapi('PublishAudioMixResponse');

export type PublishAudioMixResponseDto = z.infer<typeof PublishAudioMixResponseSchema>;

/**
 * Waveform Extraction Calculation Mode.
 */
export const WaveformModeSchema = z
  .enum(['peak', 'rms'])
  .default('peak')
  .openapi({
    description: 'Waveform calculation algorithm: peak maximum amplitude (-1..1) or Root Mean Square energy (0..1)',
    example: 'peak',
  });

export type WaveformMode = z.infer<typeof WaveformModeSchema>;

/**
 * Waveform Peak Data for Canvas Visualization.
 */
export const WaveformDataSchema = z
  .object({
    peaks: z.array(z.number()).openapi({
      description: 'Normalized array of amplitude points (-1.0 to 1.0 or 0.0 to 1.0)',
      example: [0.05, 0.42, 0.88, 0.65, 0.12],
    }),
    duration: z.number().positive().openapi({ description: 'Audio duration in seconds', example: 12.5 }),
    sampleRate: z.number().int().openapi({ description: 'Audio sample rate in Hz', example: 44100 }),
    bins: z.number().int().positive().openapi({ description: 'Total number of extracted bins/bars', example: 200 }),
    channels: z.number().int().min(1).max(2).default(1).openapi({ description: 'Number of channels', example: 1 }),
  })
  .openapi('WaveformData');

export type WaveformDataDto = z.infer<typeof WaveformDataSchema>;

/**
 * Request payload to extract waveform peak data.
 */
export const ExtractWaveformRequestSchema = z
  .object({
    audioUrl: z.string().url().openapi({
      description: 'Audio file URL to analyze',
      example: 'https://media.sophiaplatform.com/audio/sample.mp3',
    }),
    numBins: z.number().int().min(10).max(2000).default(200).openapi({
      description: 'Number of vertical bars/bins to divide timeline waveform into',
      example: 200,
    }),
    mode: WaveformModeSchema,
  })
  .openapi('ExtractWaveformRequest');

export type ExtractWaveformRequestDto = z.infer<typeof ExtractWaveformRequestSchema>;

/**
 * Interactive Timeline Player State.
 */
export const TimelinePlaybackStateSchema = z
  .object({
    currentTime: z.number().min(0).openapi({ description: 'Current playhead cursor position in seconds', example: 14.2 }),
    duration: z.number().min(0).openapi({ description: 'Total timeline duration in seconds', example: 65.0 }),
    isPlaying: z.boolean().openapi({ description: 'Whether playback is active', example: true }),
    isLooping: z.boolean().openapi({ description: 'Whether master project timeline loops', example: false }),
    playbackRate: z.number().min(0.25).max(4.0).default(1.0).openapi({ description: 'Playback speed factor', example: 1.0 }),
    masterVolume: z.number().min(0).max(2.0).default(1.0).openapi({ description: 'Master gain volume multiplier', example: 1.0 }),
    soloTrackId: z.string().nullable().openapi({ description: 'ID of soloed track if any', example: 'track_speech_01' }),
    mutedTrackIds: z.array(z.string()).default([]).openapi({ description: 'List of currently muted track IDs' }),
  })
  .openapi('TimelinePlaybackState');

export type TimelinePlaybackStateDto = z.infer<typeof TimelinePlaybackStateSchema>;

/**
 * Smart Audio Ducking Interval Region.
 */
export const AudioDuckingRegionSchema = z
  .object({
    startSec: z.number().min(0).openapi({ description: 'Start time of speech trigger', example: 2.0 }),
    endSec: z.number().min(0).openapi({ description: 'End time of speech trigger', example: 8.5 }),
    fadeStartSec: z.number().min(0).openapi({ description: 'Time when BGM starts fading down', example: 1.75 }),
    recoveryEndSec: z.number().min(0).openapi({ description: 'Time when BGM fully returns to normal volume', example: 8.9 }),
    duckingRatio: z.number().min(0).max(1).openapi({ description: 'Target ducked gain ratio (e.g. 0.20)', example: 0.2 }),
    speechClipIds: z.array(z.string()).openapi({ description: 'IDs of speech clips triggering this region' }),
  })
  .openapi('AudioDuckingRegion');

export type AudioDuckingRegionDto = z.infer<typeof AudioDuckingRegionSchema>;

/**
 * Smart Audio Ducking Analysis Response.
 */
export const AudioDuckingAnalysisResponseSchema = z
  .object({
    projectId: z.string().openapi({ description: 'Analyzed project ID', example: 'proj_mix_12345' }),
    speechTrackCount: z.number().int().min(0).openapi({ description: 'Number of active speech tracks', example: 2 }),
    bgmTrackCount: z.number().int().min(0).openapi({ description: 'Number of active BGM tracks', example: 1 }),
    totalSpeechDuration: z.number().min(0).openapi({ description: 'Total combined speech duration in seconds', example: 45.2 }),
    duckingSettings: AudioDuckingSettingsSchema,
    regions: z.array(AudioDuckingRegionSchema).openapi({ description: 'Identified ducking intervals' }),
  })
  .openapi('AudioDuckingAnalysisResponse');

export type AudioDuckingAnalysisResponseDto = z.infer<typeof AudioDuckingAnalysisResponseSchema>;
